import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PensionContext } from '../../../contexts/pension-robot/PensionContext';
import { useGetPerMonthFactorValues } from "../../../hooks/useGetPerMonthFactorValues";
import { ResultOptionDisclosure } from '../../general/Disclosure';
import { LoadingStateWrapper } from '../../general/LoadingStateWrapper';
import { CocktailWrapperScreen } from './CocktailWrapperScreen';
import { SavingButtons } from './DialogueDataSaving/PensionProgressSaver';
import { AdditionalPension } from './DisclosureUnits/AdditionalPension';
import { ChangeExpenses } from './DisclosureUnits/ChangeExpenses';
import { ChangePensionAge } from './DisclosureUnits/ChangePensionAge';
import { OtherIncomes } from './DisclosureUnits/Otherincome';
import { SaveOrInvest } from './DisclosureUnits/SaveOrInvest';
import { BackButton, NextButton } from './NavigationButtons';
import { useNetCalculation } from '../../../hooks/netGrossCalculations';
import { isGoalAchieved, isPensionCalculatedTogether } from '../../../utils/functions';
import { DEFAULT_PENSION_AGE, LAST_SECTION_INDEX, MIN_AGE_CLIENT } from '../../../config/constants';
import { useGetPensionCalcData } from '../../../api/pension-robot/useGetPensionCalcData';
import { RetirementWarning } from './RetirementWarning';

export const ResultsSection = () => {
    const { t } = useTranslation('pension');
    const {
        parameters: {personalInfo, expensesInfo},
    } = useContext(PensionContext);

    const isCalculcatedTogether = isPensionCalculatedTogether(personalInfo);
    const netValuesAtRetirementAgeOfSelf = useNetCalculation(LAST_SECTION_INDEX, false);
    const netValuesAtRetirementAgeOfPartner = useNetCalculation(LAST_SECTION_INDEX, true);

    const retirementForecastQuery = useGetPerMonthFactorValues(personalInfo.self?.age ?? MIN_AGE_CLIENT);
    const retirementForecastQueryPartner = useGetPerMonthFactorValues(personalInfo.partner?.age ?? MIN_AGE_CLIENT);

    const totalNetIncomeMontlySingle = netValuesAtRetirementAgeOfSelf.netIncome.total.monthly;
    const totalNetIncomeMontlyTogether = netValuesAtRetirementAgeOfPartner.netIncome.total.monthly;

    const goalAchiedAtPartnerPensionAge = isGoalAchieved(expensesInfo?.goal ?? 0, totalNetIncomeMontlyTogether);
    const goalAchievedAtSelfPensionAge = isGoalAchieved(expensesInfo?.goal ?? 0, totalNetIncomeMontlySingle);
    const goalAchieved = isCalculcatedTogether
        ? goalAchiedAtPartnerPensionAge.achieved && goalAchievedAtSelfPensionAge.achieved
        : goalAchievedAtSelfPensionAge.achieved;


    const getDisclosureElements = () =>
        <>
            <DisclosureWrapperAdditionalPension />
            <DisclosureWrapperInvest />
            <DisclosureWrapperSave />
            <DisclosureWrapperChangePensionAge goalAchieved={goalAchieved} />
            <DisclosureWrapperOtherIncomes />
            <DisclosureWrapperChangeExpenses />
        </>;

    return (
        <CocktailWrapperScreen

            page={5}
            isColorGoal
            title={t('pension-cocktail-fill-screen-results-title')}
            isResultsScreen={true}
        >
            <LoadingStateWrapper
                isLoading={retirementForecastQuery.isLoading || retirementForecastQueryPartner.isLoading}
                isError={retirementForecastQuery.isError || retirementForecastQueryPartner.isError}
                isIdle={retirementForecastQuery.isIdle || retirementForecastQueryPartner.isIdle}
                isFetching={retirementForecastQuery.isFetching || retirementForecastQueryPartner.isFetching}
            >
                <RetirementWarning/>
                {getDisclosureElements()}
                <div className="flex flex-row flex-wrap my-10 items-start gap-4">
                    <BackButton />
                    <NextButton nextPage={"summary"} />
                </div>
                <SavingButtons pageNum={LAST_SECTION_INDEX} />
            </LoadingStateWrapper>
        </CocktailWrapperScreen>
    );
};

const DisclosureWrapperAdditionalPension = () => {
    const { t } = useTranslation('pension');
    const { parameters: { supplementInfo } } = useContext(PensionContext);
    const [hiddenStateAdditionalPension, setHiddenStateAdditionalPension] = useState<boolean>(true);

    const isCompleted =
        (!!supplementInfo?.yearSpacePensionAccount?.monthlyPaymentAmount)
            ? supplementInfo.yearSpacePensionAccount.monthlyPaymentAmount > 0
            : false;

    const title = t('pension-result-disclosure-additional-pension-title');
    const tooltip = {
        text: t('pension-result-disclosure-additional-pension-tooltip-text')
    };

    return (
        <ResultOptionDisclosure
            title={title}
            isCompleted={isCompleted}
            tooltip={tooltip}
            hiddenState={hiddenStateAdditionalPension}
            setHiddenState={setHiddenStateAdditionalPension}
        >
            <AdditionalPension
                setHiddenState={setHiddenStateAdditionalPension}
            />
        </ResultOptionDisclosure>
    );
};

const DisclosureWrapperSave = () => {
    const { t } = useTranslation('pension');
    const { parameters: { supplementInfo } } = useContext(PensionContext);

    const isCompleted = !!supplementInfo?.self?.additionalSavingAccount?.isUsed;

    const title = t('pension-result-disclosure-save-title');
    const tooltip = {
        text: t('pension-result-disclosure-save-or-invest-tooltip-text')
    };

    return (
        <ResultOptionDisclosure
            title={title}
            isCompleted={isCompleted}
            tooltip={tooltip}
        >
            <SaveOrInvest accountType={'saving'}/> 
        </ResultOptionDisclosure>
    );
};

const DisclosureWrapperInvest= () => {
    const { t } = useTranslation('pension');
    const { parameters: { supplementInfo } } = useContext(PensionContext);

    const isCompleted = !!supplementInfo?.self?.additionalInvestmentAccount?.isUsed;

    const title = t('pension-result-disclosure-invest-title');
    const tooltip = {
        text: t('pension-result-disclosure-save-or-invest-tooltip-text')
    };

    return (
        <ResultOptionDisclosure
            title={title}
            isCompleted={isCompleted}
            tooltip={tooltip}
        >
            <SaveOrInvest accountType={'investment'}/> 
        </ResultOptionDisclosure>
    );
};

const DisclosureWrapperChangeExpenses = () => {
    const { t } = useTranslation('pension');

    const title = t('pension-result-disclosure-change-expenses-title');
    const tooltip = {
        text: t('pension-result-disclosure-change-expenses-tooltip-text')
    };

    return (
        <ResultOptionDisclosure
            title={title}
            tooltip={tooltip}
        >
            <ChangeExpenses />
        </ResultOptionDisclosure>
    );
};

const DisclosureWrapperOtherIncomes = () => {
    const { t } = useTranslation('pension');
    const { parameters: { supplementInfo } } = useContext(PensionContext);

    const isCompleted =
        (!!supplementInfo?.otherIncomeAvailable)
        && ((!!supplementInfo?.otherIncome.gift)
            || (!!supplementInfo?.otherIncome.heritage)
            || (!!supplementInfo?.otherIncome.surplusHouse)
            || (!!supplementInfo?.otherIncome.other));

    const title = t('pension-result-disclosure-other-income-title');

    return (
        <ResultOptionDisclosure
            title={title}
            isCompleted={isCompleted}
        >
            <OtherIncomes />
        </ResultOptionDisclosure>
    );
};

const DisclosureWrapperChangePensionAge: React.FC<{ goalAchieved: boolean }> = ({ goalAchieved = false }) => {
    const { t } = useTranslation('pension');
    const { parameters: { personalInfo } } = useContext(PensionContext);
    const pensionCalcData = useGetPensionCalcData();
    const legalRetirementAge = pensionCalcData.data?.legalRetirementAge ?? DEFAULT_PENSION_AGE;
    const renderWithPartner = isPensionCalculatedTogether(personalInfo);

    const isCompleted =
        personalInfo.self?.pensionAge !== legalRetirementAge
        || ((renderWithPartner) && personalInfo.partner?.pensionAge !== legalRetirementAge);

    const title = t('pension-result-disclosure-change-pension-age-title')
    const tooltip = {
        text: goalAchieved
            ? t('pension-result-disclosure-change-pension-age-goal-achieved-tooltip-text')
            : t('pension-result-disclosure-change-pension-age-goal-not-achieved-tooltip-text')
    };

    return (
        <ResultOptionDisclosure
            title={title}
            isCompleted={isCompleted}
            tooltip={tooltip}
        >
            <ChangePensionAge/>
        </ResultOptionDisclosure>
    );
};
